/* eslint-disable @typescript-eslint/no-unsafe-assignment */
import _ from 'lodash';
import { pick } from 'lodash';
import React, { Dispatch, SetStateAction, useContext, useState } from 'react';
import { ChallengeSet, ChallengeSetRequest } from '../types/ChallengeSet';
import { preProdLogger } from '../utils/log.utils';
import { useApi } from './api.context';
import { ChallengeSetAction } from './StoreModel';

export const CHALLENGE_SET_TITLE_MIN_LENGTH = 4;
export const CHALLENGE_SET_TITLE_MAX_LENGTH = 50;
export const CHALLENGE_SET_DESCRIPTION_MIN_LENGTH = 20;
export const CHALLENGE_SET_DESCRIPTION_MAX_LENGTH = 1000;

export type ToggleEditMode = () => void;
export type GetChallengeSet = (challengeSetId: string) => Promise<void>;
export type GetChallengeSets = () => Promise<void>;
export type CreateChallengeSet = (request: ChallengeSetRequest) => Promise<ChallengeSet>;
export type HandleUpdateChallengeSetAttribute = (action: string, payload: any) => void;
export type UpdateChallengeSet = () => Promise<ChallengeSet>;

export interface ChallengeSetContentValue {
  challengeSets: ChallengeSet[] | undefined;
  challengeSet: ChallengeSet | undefined;
  editedChallengeSet: ChallengeSet | undefined;
  editMode: boolean;
  toggleEditMode: ToggleEditMode;
  getChallengeSet: GetChallengeSet;
  setChallengeSets: Dispatch<SetStateAction<ChallengeSet[] | undefined>>;
  getChallengeSets: GetChallengeSets;
  createChallengeSet: CreateChallengeSet;
  handleUpdateChallengeSetAttribute: HandleUpdateChallengeSetAttribute;
  updateChallengeSet: UpdateChallengeSet;
}

const ChallengeSetContent = React.createContext<ChallengeSetContentValue>({
  challengeSets: undefined,
  challengeSet: undefined,
  setChallengeSets: () => {
     // do nothing
  },
  editedChallengeSet: undefined,
  editMode: false,
  toggleEditMode: (): void => {
     // do nothing
  },
  getChallengeSet: (_challengeSetId: string) => new Promise(() => {
     // do nothing
  }),
  getChallengeSets: () => new Promise(() => {
     // do nothing
  }),
  createChallengeSet: (_request: ChallengeSetRequest) => new Promise(() => {
     // do nothing
  }),
  handleUpdateChallengeSetAttribute: (_action: string, _payload: any) => {
     // do nothing
  },
  updateChallengeSet: () => new Promise(() => {
     // do nothing
  }),
});

const ChallengeSetProvider: React.FC = ({ children }) => {
  const [challengeSets, setChallengeSets] = useState<ChallengeSet[] | undefined>(undefined);
  const [challengeSet, setChallengeSet] = useState<ChallengeSet | undefined>();
  const [editedChallengeSet, setEditedChallengeSet] = useState<ChallengeSet | undefined>();
  const [editMode, setEditMode] = useState(false);

  const { challengeSetApi } = useApi();

  const toggleEditMode = () => {
    if (!editMode) {
      setEditedChallengeSet(challengeSet);
    }
    setEditMode(!editMode);
  };

  const getChallengeSet = async (challengeSetId: string) => {
    await challengeSetApi
      .findOneById(challengeSetId)
      .then((set: ChallengeSet) => {
        setChallengeSet(set);
      })
      .catch((err) => preProdLogger(err));
  };

  const getChallengeSets = async () => {
    await challengeSetApi
      .findAll()
      .then((challengeSetResponse: ChallengeSet[] | undefined) => {
        setChallengeSets(challengeSetResponse);
      })
      .catch((err) => preProdLogger(err));
  };

  const createChallengeSet = async (request: ChallengeSetRequest): Promise<ChallengeSet> => {
    return await challengeSetApi.create(request).then(async (cs) => {
      await getChallengeSet(cs.id || '');
      return cs;
    });
  };

  const handleUpdateChallengeSetAttribute = (action: string, payload: any) => {
    const currentChallengeSet = editedChallengeSet ? _.cloneDeep(editedChallengeSet) : new ChallengeSet();

    switch (action) {
      case ChallengeSetAction.TITLE:
        currentChallengeSet.title = payload;
        break;
      case ChallengeSetAction.DESCRIPTION:
        currentChallengeSet.description = payload;
        break;
      case ChallengeSetAction.CHALLENGE_IDS:
        currentChallengeSet.challengeIds = payload;
        break;
      case ChallengeSetAction.FEATURED:
        currentChallengeSet.featured = payload;
        break;
    }

    setEditedChallengeSet(currentChallengeSet);
  };

  const updateChallengeSet = async (): Promise<ChallengeSet> => {
    const request: ChallengeSetRequest = pick(
      editedChallengeSet,
      Object.keys(new ChallengeSetRequest())
    ) as ChallengeSetRequest;

    return await challengeSetApi.update(editedChallengeSet?.id || '', request).then(async (cs) => {
      await getChallengeSet(cs.id || '');
      return cs;
    });
  };

  const data: ChallengeSetContentValue = {
    challengeSets,
    challengeSet,
    setChallengeSets,
    editedChallengeSet,
    editMode,
    toggleEditMode,
    getChallengeSet,
    getChallengeSets,
    createChallengeSet,
    handleUpdateChallengeSetAttribute,
    updateChallengeSet,
  };

  return <ChallengeSetContent.Provider value={data}>{children}</ChallengeSetContent.Provider>;
};

const useChallengeSet = () => {
  const context = useContext(ChallengeSetContent);
  if (context === undefined) {
    throw new Error('useChallengeSet can only be used inside ChallengeSetProvider');
  }
  return context;
};

export { ChallengeSetProvider, useChallengeSet };
